import React, { memo, useCallback } from 'react';
import { BaseCommentBody } from '../BaseCommentBody';
import type {
  CommentMention,
  CommentAttachment
} from '../../containers/CommentList/CommentList.types';
import type { ReplyComponentProps } from './ReplyComponent.types';

/**
 * ReplyComponent - Displays a single reply using BaseCommentBody
 *
 * Wraps BaseCommentBody with reply-specific styling and event handling
 */
const ReplyComponentComponent: React.FC<ReplyComponentProps> = ({
  reply,
  currentUserEmail,
  menuOptions = [],
  onOptionClick,
  onTextEdit,
  onEditStart,
  onEditComplete,
  onEditCancel,
  inputConfig,
  iconOverrides,
  showEditedAt,
  submitOnBlur,
  communicationMode
}) => {
  const handleMenuOptionClick = useCallback(
    (optionId: string) => onOptionClick?.(reply.id, optionId),
    [onOptionClick, reply.id]
  );
  const handleTextEdit = useCallback(
    (
      newText: string,
      mentions?: CommentMention[],
      attachments?: (CommentAttachment | File)[]
    ) => onTextEdit?.(reply.id, newText, mentions, attachments),
    [onTextEdit, reply.id]
  );
  const handleEditStart = useCallback(
    () => onEditStart?.(reply.id),
    [onEditStart, reply.id]
  );
  const handleEditComplete = useCallback(
    (finalText: string, wasChanged: boolean) =>
      onEditComplete?.(reply.id, finalText, wasChanged),
    [onEditComplete, reply.id]
  );
  const handleEditCancel = useCallback(
    () => onEditCancel?.(reply.id),
    [onEditCancel, reply.id]
  );

  // Check if this reply is in skeleton state
  const isSkeleton = (reply as any).isSkeleton === true;
  const isEditSkeleton = (reply as any).isEditSkeleton === true;

  return (
    <div
      className={`comment-card__reply-item ${
        isSkeleton ? 'comment-card__reply-item--skeleton' : ''
      }`}
    >
      {' '}
      <BaseCommentBody
        commentAuthor={reply.commentAuthor}
        currentUserEmail={currentUserEmail}
        text={reply.text}
        commentMentions={reply.commentMentions || []}
        createdAt={reply.createdAt}
        editedAt={reply.editedAt}
        section={reply.section}
        isEditing={reply.isEditing || false}
        isEditSkeleton={isEditSkeleton}
        authorEmail={reply.commentAuthor.email}
        menuOptions={menuOptions}
        onMenuOptionClick={handleMenuOptionClick}
        onTextEdit={handleTextEdit}
        onEditStart={handleEditStart}
        onEditComplete={handleEditComplete}
        onEditCancel={handleEditCancel}
        headerClassName="comment-card__reply-header"
        contentClassName="comment-card__reply-content"
        inputConfig={inputConfig}
        iconOverrides={iconOverrides}
        showEditedAt={showEditedAt}
        submitOnBlur={submitOnBlur}
        attachments={reply.attachments}
        communicationMode={communicationMode}
      />
    </div>
  );
};

export const ReplyComponent = memo(ReplyComponentComponent);
